/**
 * The session: applying settings, invites, starting and switching games,
 * spectating, and the per-mode hand-off to Ctf.ts and Btr.ts. The account
 * lives in Login.ts.
 */
import { GAME_TYPES } from '@airmash/protocol';
import { isSelectableFlag } from '../services/Flags';
import { regions as gamesDataRegions } from '../services/GamesData';
import { uiState } from '../ui/UIState';
import { update as btrUpdate, wipe as btrWipe } from './Btr';
import { prep as ctfPrep, update as ctfUpdate, wipe as ctfWipe } from './Ctf';
import { GAME_DEFAULTS } from './Config';
import { device } from './Device';
import { setCtfSpawnLinesEnabled as graphicsSetCtfSpawnLinesEnabled } from './Graphics';
import { ajaxPost } from './Http';
import { setMouseMode as inputSetMouseMode, wipe as inputWipe } from './Input';
import { wipe as mobsWipe } from './Mobs';
import { shutdown as networkShutdown, spectateNext as networkSpectateNext } from './Network';
import { get as playersGet, setMyFlag as playersSetMyFlag, wipe as playersWipe } from './Players';
import { wipeReel } from './Reel';
import { GAME_STATE_CODES, session, startSession, type GameTarget } from './Session';
import { setSettings, setSettingsUnsaved, settings } from './Settings';
import { levelUp as soundLevelUp } from './Sound';
import { randomID as toolsRandomID } from './Tools';
import {
  gameStart as uiGameStart,
  reconnection as uiReconnection,
  setGameNotification as uiSetGameNotification,
  showSpectator as uiShowSpectator,
  updateSound as uiUpdateSound,
} from './UI';
import { wipe as particlesWipe } from './particles/Particles';
import type { GameRegion } from './types/Games';
import { version } from './Version';

/**
 * Applies the saved settings to game state, UI, and input modes: at boot, and
 * again when the account's copy arrives. The settings the menu shows are
 * mirrored into uiState by GameAPI.syncState, which the boot and the login
 * run after this.
 */
export function applySettingsToGame(): void {
  // Generate unique player ID if it doesn't exist
  if (settings.id == null) {
    setSettings({ id: toolsRandomID(16) });
  }

  if (settings.name != null) {
    uiState.playerName = settings.name;
  }

  // The saved region is the lobby's initial selection ('closest' or a region
  // id); the session's target is only ever a real region, set when a game starts.
  if (settings.region != null) {
    uiState.savedRegion = settings.region;
  }

  // The saved flag, lowercased since the original client saved what was typed; one the client cannot show is skipped
  const savedFlag = settings.flag?.toLowerCase();
  if (savedFlag !== undefined && isSelectableFlag(savedFlag)) {
    playersSetMyFlag(savedFlag);
  }

  // Default sound to enabled if not explicitly set
  if (settings.sound == null) {
    setSettingsUnsaved({ sound: true });
  }

  // Enable mouse mode if setting is enabled
  if (settings.mousemode) {
    inputSetMouseMode(true, false);
  }

  // Graphics keeps its own copy of the spawn lines setting for the CTF draw
  graphicsSetCtfSpawnLinesEnabled(settings.game?.ctfSpawnLines ?? GAME_DEFAULTS.ctfSpawnLines);

  // Mirrors the sound setting for the toolbar's icon
  uiUpdateSound();
}

/** An invite's inviter name is cut to the logon name field's limit. */
const INVITER_NAME_MAX = 20;

/** The query params an invite link carries. */
const INVITE_PARAMS = ['region', 'room', 'inviter'] as const;

/**
 * Parses URL to auto-select a game server from an invite link.
 * Supports two formats:
 * - Query params (preferred): ?region=eu&room=ffa1&inviter=PlayerName
 * - Legacy hash format: #eu-ffa1
 * Populates uiState.invite for the UI banner, clears the invite from URL.
 */
/** Whether this page was opened through an invite link, whose region is not a choice to remember. */
let invited = false;

export function selectGameFromUrlParams(regions: ReadonlyArray<GameRegion>) {
  // Try query parameters first (preferred format)
  const params = new URLSearchParams(window.location.search);
  let regionId = params.get('region');
  let roomId = params.get('room');
  // Real links carry a player name; a crafted one can carry any text, which
  // would print in full as "___ invited you to".
  const inviterName = params.get('inviter')?.slice(0, INVITER_NAME_MAX) || undefined;

  // Fall back to legacy hash format: #region-room
  if (!regionId || !roomId) {
    const hash = window.location.hash;
    if (hash && hash !== '#reload' && hash.length >= 4 && hash.length <= 50) {
      const parts = hash.substring(1).split('-');
      if (parts.length >= 2) {
        regionId = parts[0]!;
        roomId = parts[1]!;
      }
    }
  }

  // Need at least region and room
  if (!regionId || !roomId) {
    return;
  }

  // Take the invite out of the URL so a refresh doesn't replay it, keeping
  // any other params (such as ?dev)
  const url = new URL(window.location.href);
  for (const key of INVITE_PARAMS) url.searchParams.delete(key);
  url.hash = '';
  history.replaceState(null, '', url);

  // Look up this region and room in games data
  const region = regions.find((r) => r.id === regionId);
  const gameServer = region?.games.find((g) => g.id === roomId);

  if (region && gameServer) {
    // Valid invite - populate UIState with resolved names
    uiState.invite = {
      regionId,
      roomId,
      inviterName,
      isValid: true,
      regionName: region.name,
      gameName: gameServer.nameShort,
    };

    invited = true;
  } else {
    // Invalid invite - store for UI to show error banner
    uiState.invite = {
      regionId,
      roomId,
      inviterName,
      isValid: false,
    };
  }
}

/** Whether the page is on its way back to the title, so the disconnect that follows shows no notice. */
let reloading = false;

export function isReloading(): boolean {
  return reloading;
}

/** Redirects to the root page and marks the game as reloading */
export function redirRoot(): void {
  reloading = true;
  window.location.href = '/';
}

/**
 * Reconnects, to another room when `gameServerId` ("region-room", e.g.
 * "us-ffa1") is given. An id the games data can't resolve is ignored rather
 * than reconnecting to the current server under a stale name.
 */
export function switchGame(gameServerId?: string): void {
  let target = session.target;
  if (gameServerId != null) {
    const [regionId, roomId] = gameServerId.split('-');
    // The server info from the games list, the game side's source of truth
    const region = gamesDataRegions.find((r) => r.id === regionId);
    const gameServer = region?.games.find((g) => g.id === roomId);
    if (regionId === undefined || roomId === undefined || region === undefined || gameServer === undefined) {
      console.warn(`switchGame: no such game ${gameServerId}`);
      return;
    }

    target = { kind: 'server', regionId, roomId, host: gameServer.host, path: gameServer.path };

    // Update UI state
    uiState.currentGameId = roomId;
    uiState.currentGameName = gameServer.nameShort;
    uiState.currentRegionId = regionId;
    uiState.currentRegionName = region.name;
  }
  if (target === null) {
    console.error('switchGame: no game to return to');
    return;
  }
  // Before the shutdown, so the close it causes reads CONNECTING and shows no reconnect notice
  session.state = GAME_STATE_CODES.CONNECTING;
  networkShutdown();
  inputWipe();
  particlesWipe();
  playersWipe();
  mobsWipe();
  uiReconnection();
  start(target, session.myName);
}

/**
 * Starts the game and connects to the selected server. `regionPreference` is
 * the region to remember for next time, 'closest' or a region id; a switch
 * passes none and leaves the saved choice alone, where legacy pinned the
 * region of the room you switched to.
 */
export function start(target: GameTarget, playerName: string, fromMainPage = false, regionPreference?: string): void {
  if (fromMainPage && session.state === GAME_STATE_CODES.CONNECTING) {
    return;
  }

  // Clear the main page if we're on it
  if (session.state === GAME_STATE_CODES.LOGIN) {
    wipeReel();
  }
  startSession(target, playerName);

  // Any change to player name gets saved to settings upon game start
  const changes: { name: string; region?: string } = {
    name: playerName,
  };

  // An invite's region is not a choice to remember, and a dev connect has none
  if (target.kind === 'server' && regionPreference !== undefined && !invited) {
    changes.region = regionPreference;
  }

  // Persist settings to storage
  setSettings(changes);

  uiGameStart(fromMainPage);

  // Usage telemetry
  ajaxPost('/enter', {
    id: settings.id,
    name: playerName,
    game: session.serverId,
    source: document.referrer,
    mode: device.mobile ? 1 : 0,
    version,
    switch: !fromMainPage,
  }).catch(() => {
    // Telemetry only: a missing backend is not the player's problem
  });
}

/** Prepares game UI and state for a new session. Called on LOGIN message from server. */
export function prep(): void {
  // Remove anything that may persist from previous game type
  wipe();

  if (session.gameType === GAME_TYPES.CTF) {
    ctfPrep();
  }
}

/** Clears game-mode-specific state and graphics */
export function wipe(): void {
  ctfWipe();
  btrWipe();
}

/** Handles GAME_SPECTATE message to enter spectator mode */
export function spectate(playerId: number): void {
  if (session.spectatingID == null && session.gameType !== GAME_TYPES.BTR) {
    uiSetGameNotification({
      type: 'alert',
      title: 'SPECTATOR MODE',
      // Legacy said "Click on Respawn", but had no Respawn button. Name the
      // real way back: the sidebar planes / digit keys on desktop, and the
      // spectator overlay's RESPAWN button (which opens loadout) on mobile.
      message: device.mobile
        ? 'Tap RESPAWN to pick a plane and resume playing'
        : 'Click a plane on the left, or press 1-5, to resume playing',
      showReconnect: false,
      timeoutMs: 4000,
    });
  }
  session.spectatingID = playerId;

  const player = playersGet(playerId);
  const playerName = player == null ? '' : player.name;
  uiShowSpectator('spectating', playerName);
}

/** Pending spectator auto-switch state. Only one is queued at a time — if a
 *  new death comes in while one is pending, the older one is replaced. */
let pendingSwitchPlayerId: number | null = null;
let pendingSwitchTimeoutId: ReturnType<typeof setTimeout> | null = null;

/** Switches to spectating another player after a 2s delay, unless the
 *  spectated player respawns first (see cancelSpectatorSwitchOnRespawn). */
export function spectatorSwitch(id: number): void {
  if (pendingSwitchTimeoutId !== null) {
    clearTimeout(pendingSwitchTimeoutId);
  }
  pendingSwitchPlayerId = id;
  pendingSwitchTimeoutId = setTimeout(() => {
    pendingSwitchPlayerId = null;
    pendingSwitchTimeoutId = null;
    if (id === session.spectatingID) {
      networkSpectateNext();
    }
  }, 2000);
}

/** Cancels a pending spectator auto-switch if it's for the given player id.
 *  Called when a player respawns — so we keep watching them instead of
 *  cycling to a random other player. */
export function cancelSpectatorSwitchOnRespawn(id: number): void {
  if (pendingSwitchPlayerId === id && pendingSwitchTimeoutId !== null) {
    clearTimeout(pendingSwitchTimeoutId);
    pendingSwitchPlayerId = null;
    pendingSwitchTimeoutId = null;
  }
}

/** Displays the level up notification. (Legacy's "FEATURE UNLOCKED" line read
 *  an unlock table that was always empty, so it isn't ported.) */
export function showLevelUp(level: number): void {
  uiState.levelUp = { level };
  soundLevelUp();
}

/** Frame update handler */
export function update(isResize: boolean): void {
  switch (session.gameType) {
    case GAME_TYPES.CTF:
      ctfUpdate(isResize);
      break;
    case GAME_TYPES.BTR:
      btrUpdate(isResize);
      break;
  }
}
